import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

// Get API base URL from environment or use proxy fallback
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const navigate = useNavigate();

  // Set up axios defaults
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Check if user is logged in on app start
  useEffect(() => {
      const checkAuth = async () => {
    if (token) {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/auth/profile`);
        setUser(response.data.user);
      } catch (error) {
        console.error('Auth check failed:', error);
        logout({ reason: 'Your session has expired. Please sign in again.' });
      }
    }
    setLoading(false);
  };

    checkAuth();
  }, [token]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/login`, {
        email,
        password,
      });

      const { token, access_token, user } = response.data;
      const authToken = token || access_token; // Use either token format

      setToken(authToken);
      setUser(user);
      localStorage.setItem('token', authToken);

      toast.success('Login successful!');

      // Redirect based on user role
      if (user.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }

      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || error.response?.data?.error || 'Login failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const register = async (userData) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/register`, userData);

      toast.success('Registration successful! Please login.');
      navigate('/login');

      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || error.response?.data?.error || 'Registration failed';

      // Check for specific error messages for better user feedback
      if (message.toLowerCase().includes('email already registered')) {
        toast.error('This email is already registered. Please use a different email or login.');
      } else {
        toast.error(message);
      }

      return { success: false, error: message };
    }
  };

  // Public pages anyone can read without an account. A stale token must never bounce a
  // visitor off one of these; they are the marketing site and the article library.
  const PUBLIC_PATHS = [
    '/', '/fbr-digital-invoicing', '/pra-digital-invoicing', '/tax-filing', '/odoo-integration',
  ];
  const onPublicPage = () => {
    const p = window.location.pathname;
    return PUBLIC_PATHS.includes(p) || p.startsWith('/guides');
  };

  const logout = (options = {}) => {
    // logout() is also called when an auth check fails, so it must be able to say why.
    // Without this an expired session greeted the user with "Logged out successfully".
    const { reason } = options;
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];

    if (reason) {
      // An expired session found while reading a public page is not worth interrupting
      // anyone over. Clear the stale token and let them carry on reading.
      if (onPublicPage()) return;
      navigate('/login');
      toast.error(reason);
    } else {
      navigate('/login');
      toast.success('Logged out successfully');
    }
  };

  const updateProfile = async (profileData) => {
    try {
      const response = await axios.put(`${API_BASE_URL}/api/auth/profile`, profileData);
      setUser(response.data.user);
      toast.success('Profile updated successfully!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.error || 'Profile update failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      await axios.post(`${API_BASE_URL}/api/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword,
      });

      toast.success('Password changed successfully!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.error || 'Password change failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    updateProfile,
    changePassword,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isTenantAdmin: user?.role === 'tenant_admin',
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};